import type { AxiosError } from "axios";
import { serverMsg, translateServerError } from "@/lib/serverErrors";

/**
 * Rewrites an Axios error's `.message` in place so anything that reads it
 * later (redux-toolkit's `action.error.message` on a rejected thunk being
 * the big one — none of the async thunks in this app call
 * `rejectWithValue`, so RTK's default rejection handler is exactly
 * `error.message`) shows something a user can act on instead of Axios's
 * own generic "Request failed with status code 400".
 *
 * Order of preference: the backend's own `{ error: "..." }` body (what
 * every controller in this app sends — see e.g. institutionController.js),
 * then a friendly fallback for no-response/5xx cases. Left untouched if
 * neither applies (e.g. a client-side abort) so existing `.message` reads
 * survive.
 */
export function toFriendlyErrorMessage(error: AxiosError): string {
  const data = error.response?.data as { error?: unknown; message?: unknown } | undefined;
  const backendMessage = data?.error ?? data?.message;
  if (typeof backendMessage === "string" && backendMessage.trim()) {
    return translateServerError(backendMessage);
  }
  if (!error.response) {
    return serverMsg("net.offline", "Network error — check your connection and try again.");
  }
  if (error.response.status >= 500) {
    return serverMsg("net.server", "Something went wrong on our end. Please try again in a moment.");
  }
  return serverMsg("net.generic", "Something went wrong. Please try again.");
}

/** Response-interceptor rejection handler: rewrites `error.message`, then re-rejects. */
export function applyFriendlyErrorMessage(error: AxiosError) {
  const data = error.response?.data as { error?: unknown; message?: unknown } | undefined;
  if (data && typeof data === "object") {
    if (typeof data.error === "string") data.error = translateServerError(data.error);
    if (typeof data.message === "string") data.message = translateServerError(data.message);
  }
  error.message = toFriendlyErrorMessage(error);
  return Promise.reject(error);
}
